import React, { Component } from "react";
import { withAuth } from "../Auth/WithAuth";
import { authenticatedFetch } from "../../utils/apiClient";
import "./Admin.css";

let isMounted = false;

export default withAuth(
  class AdminUserManagement extends Component {
    constructor(props) {
      super(props);
      this.state = {
        adminUsers: [],
        showAddUserModal: false,
        showResetPasswordModal: false,
        selectedUser: null,
        newUser: {
          username: "",
          password: "",
          role: "admin"
        },
        newPassword: "",
        error: "",
        success: "",
        isSubmitting: false,
        updated: new Date().toLocaleString()
      };

      this.getAdminUsers = this.getAdminUsers.bind(this);
      this.addAdminUser = this.addAdminUser.bind(this);
      this.removeAdminUser = this.removeAdminUser.bind(this);
      this.resetPassword = this.resetPassword.bind(this);
      this.handleInputChange = this.handleInputChange.bind(this);
      this.openAddUserModal = this.openAddUserModal.bind(this);
      this.openResetPasswordModal = this.openResetPasswordModal.bind(this);
      this.closeModals = this.closeModals.bind(this);
    }

    async componentDidMount() {
      isMounted = true;
      this.getAdminUsers();
    }

    componentWillUnmount() {
      isMounted = false;
    }

    async getAdminUsers() {
      try {
        const response = await authenticatedFetch("/api/admin/getAdminUsers", {
          method: "GET"
        });

        if (response.ok) {
          const adminUsers = await response.json();
          this.setState({ adminUsers, updated: new Date().toLocaleString() });
        } else {
          this.setState({ error: "Failed to load admin users" });
        }
      } catch (err) {
        console.error("Error fetching admin users:", err);
        this.setState({ error: "Network error loading admin users" });
      }
    }

    handleInputChange(e) {
      const { name, value } = e.target;
      if (name === "newPassword") {
        this.setState({ newPassword: value, error: "", success: "" });
      } else {
        this.setState({
          newUser: { ...this.state.newUser, [name]: value },
          error: "",
          success: ""
        });
      }
    }

    openAddUserModal() {
      this.setState({
        showAddUserModal: true,
        newUser: { username: "", password: "", role: "admin" },
        error: "",
        success: ""
      });
    }

    openResetPasswordModal(user) {
      this.setState({
        showResetPasswordModal: true,
        selectedUser: user,
        newPassword: "",
        error: "",
        success: ""
      });
    }

    closeModals() {
      this.setState({
        showAddUserModal: false,
        showResetPasswordModal: false,
        selectedUser: null,
        newUser: { username: "", password: "", role: "admin" },
        newPassword: "",
        error: "",
        success: "",
        isSubmitting: false
      });
    }

    async addAdminUser() {
      const { username, password } = this.state.newUser;

      if (!username || !password) {
        this.setState({ error: "Username and password are required" });
        return;
      }

      if (password.length < 6) {
        this.setState({ error: "Password must be at least 6 characters" });
        return;
      }

      this.setState({ isSubmitting: true });

      try {
        const response = await authenticatedFetch("/api/admin/addAdminUser", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(this.state.newUser)
        });

        if (response.ok) {
          this.setState({ success: "Admin user added successfully" });
          this.getAdminUsers();
          setTimeout(() => this.closeModals(), 1500);
        } else {
          const errorData = await response.json();
          this.setState({
            error: errorData.error || "Failed to add admin user",
            isSubmitting: false
          });
        }
      } catch (err) {
        console.error("Error adding admin user:", err);
        this.setState({
          error: "Network error adding admin user",
          isSubmitting: false
        });
      }
    }

    async removeAdminUser(username) {
      if (username === this.props.auth.currentUser.username) {
        alert("You cannot delete your own account");
        return;
      }

      if (!window.confirm(`Are you sure you want to delete admin user "${username}"?`)) {
        return;
      }

      try {
        const response = await authenticatedFetch(`/api/admin/removeAdminUser/${username}`, {
          method: "DELETE"
        });

        if (response.ok) {
          this.setState({ success: "Admin user removed successfully" });
          this.getAdminUsers();
          setTimeout(() => this.setState({ success: "" }), 3000);
        } else {
          const errorData = await response.json();
          this.setState({ error: errorData.error || "Failed to remove admin user" });
        }
      } catch (err) {
        console.error("Error removing admin user:", err);
        this.setState({ error: "Network error removing admin user" });
      }
    }

    async resetPassword() {
      const { selectedUser, newPassword } = this.state;

      if (!newPassword) {
        this.setState({ error: "New password is required" });
        return;
      }

      if (newPassword.length < 6) {
        this.setState({ error: "Password must be at least 6 characters" });
        return;
      }

      this.setState({ isSubmitting: true });

      try {
        const response = await authenticatedFetch("/api/admin/resetAdminPassword", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: selectedUser.username,
            newPassword
          })
        });

        if (response.ok) {
          this.setState({ success: "Password reset successfully" });
          setTimeout(() => this.closeModals(), 1500);
        } else {
          const errorData = await response.json();
          this.setState({
            error: errorData.error || "Failed to reset password",
            isSubmitting: false
          });
        }
      } catch (err) {
        console.error("Error resetting password:", err);
        this.setState({
          error: "Network error resetting password",
          isSubmitting: false
        });
      }
    }

    render() {
      if (this.props.authenticated === null) return null;

      const { adminUsers, error, success, isSubmitting, updated } = this.state;

      const userRows = adminUsers.map((user, index) => (
        <tr key={index}>
          <td>{user.username}</td>
          <td>{user.role || "admin"}</td>
          <td>{user.lastLogin ? new Date(user.lastLogin).toLocaleString() : "Never"}</td>
          <td>{new Date(user.created).toLocaleString()}</td>
          <td>
            {(index !== 0 || user.username === this.props.auth.currentUser.username) && (
              <button
                className="btn btn-warning btn-sm me-2"
                onClick={() => this.openResetPasswordModal(user)}
                style={{ marginRight: "10px" }}
              >
                Reset Password
              </button>
            )}
            {index !== 0 && <button
              className="btn btn-danger btn-sm"
              onClick={() => this.removeAdminUser(user.username)}
              disabled={user.username === this.props.auth.currentUser.username}
            >
              Remove
            </button>}
          </td>
        </tr>
      ));

      return (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4>Admin User Management</h4>
              <p>Updated: {updated}</p>
            </div>
            <button
              className="btn btn-primary"
              onClick={this.openAddUserModal}
            >
              Add Admin User
            </button>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success" role="alert">
              {success}
            </div>
          )}

          <h5>Admin Users ({userRows.length})</h5>
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Last Login</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>{userRows}</tbody>
          </table>

          {/* Add User Modal */}
          {this.state.showAddUserModal && (
            <div className="Modal" onClick={(e) => e.target === e.currentTarget && this.closeModals()} style={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}>
              <div className="card" style={{
                maxWidth: '400px',
                backgroundColor: '#343a40',
                color: 'white',
                border: '1px solid #6c757d',
                padding: '20px'
              }}>
                <h4 style={{ marginBottom: '20px', textAlign: 'center' }}>Add Admin User</h4>

                {error && (
                  <div className="alert alert-danger" style={{ fontSize: '14px', padding: '8px 12px' }}>
                    {error}
                  </div>
                )}

                {success && (
                  <div className="alert alert-success" style={{ fontSize: '14px', padding: '8px 12px' }}>
                    {success}
                  </div>
                )}

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Username *</label>
                  <input
                    type="text"
                    name="username"
                    value={this.state.newUser.username}
                    onChange={this.handleInputChange}
                    className="form-control form-control-sm"
                    disabled={isSubmitting}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Password *</label>
                  <input
                    type="password"
                    name="password"
                    value={this.state.newUser.password}
                    onChange={this.handleInputChange}
                    className="form-control form-control-sm"
                    disabled={isSubmitting}
                  />
                </div>

                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={this.closeModals}
                    disabled={isSubmitting}
                    style={{ marginRight: '10px', minWidth: '80px' }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-info btn-sm"
                    onClick={this.addAdminUser}
                    disabled={isSubmitting}
                    style={{ minWidth: '80px' }}
                  >
                    {isSubmitting ? "Adding..." : "Add User"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Reset Password Modal */}
          {this.state.showResetPasswordModal && (
            <div className="Modal" onClick={(e) => e.target === e.currentTarget && this.closeModals()} style={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}>
              <div className="card" style={{
                maxWidth: '400px',
                backgroundColor: '#343a40',
                color: 'white',
                border: '1px solid #6c757d',
                padding: '20px'
              }}>
                <h4 style={{ marginBottom: '20px', textAlign: 'center' }}>Reset Password</h4>
                <p style={{ textAlign: 'center', marginBottom: '20px' }}>
                  Reset password for: <strong>{this.state.selectedUser && this.state.selectedUser.username}</strong>
                </p>

                {error && (
                  <div className="alert alert-danger" style={{ fontSize: '14px', padding: '8px 12px' }}>
                    {error}
                  </div>
                )}

                {success && (
                  <div className="alert alert-success" style={{ fontSize: '14px', padding: '8px 12px' }}>
                    {success}
                  </div>
                )}

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 'bold' }}>New Password *</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={this.state.newPassword}
                    onChange={this.handleInputChange}
                    className="form-control form-control-sm"
                    disabled={isSubmitting}
                  />
                </div>

                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={this.closeModals}
                    disabled={isSubmitting}
                    style={{ marginRight: '10px', minWidth: '80px' }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={this.resetPassword}
                    disabled={isSubmitting}
                    style={{ minWidth: '80px' }}
                  >
                    {isSubmitting ? "Resetting..." : "Reset"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
  }
);


// WEBPACK FOOTER //
// ./src/Components/Admin/AdminUserManagement.js